import { ArrowRight, Calendar, Loader2, Lock } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { useDevFeatures } from "@/hooks/useDevFeatures";
import { useUserProfile } from "@/hooks/useUserProfile";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { api } from "@/services/api";
import type { TimelineAssignment, TimelineResponse, TimelineWeek } from "@/types";

interface ChipStyle {
  label: string;
  className: string;
}

interface RowStatus {
  chip: ChipStyle;
  button: string | null;
  dimmed: boolean;
}

const CHIP_STYLES = {
  open: "border-brand bg-[#F0F8F2] text-[#1E6F4F]",
  locked: "border-[#DDD9CA] bg-[#F4F2E9] text-[#8B8F84]",
  review: "border-[#E4D3A1] bg-[#FBF6E7] text-[#8A6D1F]",
  scored: "border-[#013220] bg-[#013220] text-[#F2EFE3]",
  missed: "border-red-200 bg-red-50 text-red-700",
};

export default function TimelinePage() {
  const navigate = useNavigate();
  const { enrollment, isLoading: enrollmentLoading } = useEnrollment();
  const { userProfile, isLoading: profileLoading } = useUserProfile();
  const { devFeaturesEnabled } = useDevFeatures();
  const [isLoading, setIsLoading] = useState(true);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);

  const loadTimelineData = useCallback(async () => {
    if (enrollmentLoading || profileLoading) return;

    if (!enrollment) {
      navigate("/register");
      return;
    }

    if (!userProfile) return;

    try {
      const timelineData = await api.getTimeline(enrollment.programme.id as string, userProfile.id);
      setTimeline(timelineData);
    } catch (error) {
      console.error("Failed to load timeline data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [enrollment, userProfile, enrollmentLoading, profileLoading, navigate]);

  useEffect(() => {
    loadTimelineData();
  }, [loadTimelineData]);

  const isFinalReview = (review: TimelineAssignment["review"]) =>
    review != null && (review.status === "admin_confirmed" || review.status === "admin_updated");

  const getRowStatus = (
    assignment: TimelineAssignment,
    weekStatus: string,
    isUnweighted: boolean
  ): RowStatus => {
    const isLocked =
      !devFeaturesEnabled && (weekStatus === "locked" || assignment.time_status === "locked");

    if (isLocked) {
      return {
        chip: { label: "LOCKED", className: CHIP_STYLES.locked },
        button: null,
        dimmed: true,
      };
    }

    if (assignment.review?.status === "failed") {
      return {
        chip: { label: "REVIEW FAILED", className: CHIP_STYLES.missed },
        button: "VIEW",
        dimmed: false,
      };
    }

    if (isFinalReview(assignment.review)) {
      const score = assignment.review?.final_score;
      return {
        chip: {
          label: `FEEDBACK ${score}${isUnweighted ? " · UNWEIGHTED" : ""}`,
          className: CHIP_STYLES.scored,
        },
        button: "FEEDBACK",
        dimmed: false,
      };
    }

    if (assignment.submission) {
      // Submitted but no final sign-off yet: while the window is open the
      // participant can still revise; after close it's with the review team.
      if (assignment.time_status === "closed") {
        return {
          chip: { label: "IN REVIEW", className: CHIP_STYLES.review },
          button: "VIEW",
          dimmed: false,
        };
      }
      return {
        chip: { label: "SUBMITTED", className: CHIP_STYLES.open },
        button: "VIEW",
        dimmed: false,
      };
    }

    if (assignment.time_status === "closed") {
      return {
        chip: { label: "DEADLINE PASSED", className: CHIP_STYLES.missed },
        button: null,
        dimmed: false,
      };
    }

    return {
      chip: { label: "OPEN FOR SUBMISSION", className: CHIP_STYLES.open },
      button: "SUBMIT",
      dimmed: false,
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "Europe/London",
    });
    const timeStr = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
    return `${dateStr} ${timeStr}`;
  };

  const programmeNotStarted = timeline
    ? new Date() < new Date(timeline.programme.start_date)
    : false;

  const programmeCompleted = timeline?.programme.status === "completed";

  // With dev_features enabled the API reports every week as "active";
  // fall back to the assignments' time_status to tell future weeks apart.
  const displayWeekStatus = (week: TimelineWeek): TimelineWeek["status"] => {
    if (week.status !== "active") return week.status;
    if (week.assignments.length === 0) return "active";
    if (week.assignments.every((a) => a.time_status === "locked")) return "locked";
    // Every window has closed — the week is over even if the API still
    // reports it active (it only marks weeks completed on full submission)
    if (week.assignments.every((a) => a.time_status === "closed")) return "completed";
    return "active";
  };

  const weekOverview = (week: TimelineWeek) => {
    const submitted = week.assignments.filter((a) => a.submission).length;
    const total = week.assignments.length || 1;
    const status = displayWeekStatus(week);
    if (status === "locked") {
      return { status: "Locked", statusClass: "text-content-muted", pct: 0 };
    }
    if (status === "completed" || programmeCompleted) {
      return { status: "Complete", statusClass: "text-[#1E6F4F]", pct: 100 };
    }
    return {
      status: "In progress",
      statusClass: "text-[#1E6F4F]",
      pct: Math.max(8, Math.round((submitted / total) * 100)),
    };
  };

  const weekHeader = (week: TimelineWeek) => {
    const submitted = week.assignments.filter((a) => a.submission).length;
    const status = displayWeekStatus(week);
    if (status === "locked") {
      const firstStart = week.assignments[0]?.start_date;
      return {
        bg: "bg-[#8A9990]",
        sub: programmeNotStarted
          ? "Unlocks when programme starts"
          : firstStart
            ? `Unlocks ${formatDate(firstStart)}`
            : "Locked",
        badge: "NOT AVAILABLE YET",
      };
    }
    if (status === "completed" || programmeCompleted) {
      return {
        bg: "bg-[#1E5B41]",
        sub: `${submitted}/${week.assignments.length} Work Packages Submitted`,
        badge:
          week.week_score !== null
            ? `COMPLETE · WEEKLY FEEDBACK ${Math.round(week.week_score)}`
            : "COMPLETE",
      };
    }
    return {
      bg: "bg-[#013220]",
      sub: `${submitted}/${week.assignments.length} Work Packages Submitted`,
      badge: "IN PROGRESS",
    };
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-content-muted" />
        </div>
      </DashboardLayout>
    );
  }

  if (!timeline) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-content-muted">Unable to load timeline</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto flex flex-col gap-[26px]">
        {/* Page Header */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
            Schedule
          </p>
          <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-[-.015em] text-content-foreground">
            Work Packages
          </h1>
          <p className="mt-1.5 text-[15px] text-[#5C6E63]">
            The same routine every week: one work package released Monday, one Thursday, at 13:00
            (UK time)
          </p>
        </div>

        {/* Programme Not Started Banner */}
        {programmeNotStarted && (
          <div className="flex items-center gap-3 rounded-xl border border-[#E4D3A1] bg-[#FBF6E7] p-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#F3E7C4]">
              <Calendar className="h-5 w-5 text-[#8A6D1F]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#6B5417]">
                Programme starts {formatDate(timeline.programme.start_date)}
              </h3>
              <p className="text-sm text-[#8A6D1F]">
                All assignments will unlock when the programme begins
              </p>
            </div>
          </div>
        )}

        {/* Week overview strip */}
        <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          {timeline.weeks.map((week) => {
            const o = weekOverview(week);
            return (
              <div
                key={week.week_number}
                className="flex flex-col gap-[9px] rounded-xl border border-content-border bg-card px-[18px] py-4"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-bold tracking-[.14em] text-[#5C6E63]">
                    WEEK {week.week_number}
                  </div>
                  <span className={`font-serif text-xs italic ${o.statusClass}`}>{o.status}</span>
                </div>
                <div className="h-[5px] overflow-hidden rounded-full bg-[#EFECDE]">
                  <div className="h-full rounded-full bg-brand" style={{ width: `${o.pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Weeks */}
        <div className="flex flex-col gap-[22px]">
          {timeline.weeks.map((week) => {
            // Lock icon and buttons follow the display status, but dev
            // features keep locked weeks interactive for testing
            const isLocked = displayWeekStatus(week) === "locked" && !devFeaturesEnabled;
            const header = weekHeader(week);

            return (
              <div
                key={week.week_number}
                className="overflow-hidden rounded-[14px] border border-content-border bg-card"
              >
                {/* Week Header */}
                <div
                  className={`flex items-center gap-4 px-7 py-[22px] text-[#F2EFE3] ${header.bg}`}
                >
                  <div>
                    <div className="text-2xl font-extrabold tracking-[-.01em]">
                      Week {week.week_number}
                    </div>
                    <div className="mt-0.5 font-serif text-[13.5px] italic opacity-75">
                      {header.sub}
                    </div>
                  </div>
                  {isLocked && <Lock className="h-5 w-5 opacity-70" strokeWidth={1.7} />}
                  <div className="ml-auto text-right text-[11px] font-bold tracking-[.14em] opacity-70">
                    {header.badge}
                  </div>
                </div>

                {/* Assignments */}
                <div className="flex flex-col gap-3.5 px-7 py-5">
                  {week.assignments.length === 0 ? (
                    <div className="py-4 text-center text-sm text-content-muted">
                      No work packages for this week yet
                    </div>
                  ) : (
                    week.assignments.map((assignment) => {
                      // WP number is the global 1..N sequence; the unweighted
                      // practice run is the earliest window (WP 1).
                      const workPackageNumber =
                        assignment.work_package_number ?? assignment.assignment_number;
                      const isUnweighted = workPackageNumber === 1;
                      const status = getRowStatus(assignment, week.status, isUnweighted);
                      const showButton =
                        status.button !== null && !isLocked && assignment.topic_id !== null;

                      return (
                        <div
                          key={assignment.id}
                          className={`flex flex-wrap items-center gap-x-[18px] gap-y-3 rounded-[11px] border border-[#ECE8DA] bg-[#FDFBF4] px-5 py-4 ${status.dimmed ? "opacity-[.62]" : ""}`}
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="text-[16.5px] font-bold text-card-foreground">
                                Work Package {workPackageNumber}
                              </div>
                              <span
                                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-[5px] text-[10.5px] font-bold tracking-[.1em] ${status.chip.className}`}
                              >
                                {status.chip.label}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center gap-2.5 text-[13px] text-[#5C6E63]">
                              {formatDate(assignment.start_date)}
                              <ArrowRight
                                className="h-[13px] w-[13px] text-[#A6AFA3]"
                                strokeWidth={2}
                              />
                              {formatDate(assignment.deadline)}
                            </div>
                          </div>
                          {showButton && (
                            <button
                              onClick={() => navigate(`/submissions/${assignment.id}`)}
                              className="w-full flex-none rounded-lg bg-[#013220] px-[22px] py-[11px] text-[11.5px] font-bold tracking-[.12em] text-[#F2EFE3] transition-colors hover:bg-[#0A4530] sm:ml-auto sm:w-auto"
                            >
                              {status.button}
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
